import { DropdownItem, Text, Tooltip } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useSession } from 'components/auth/useSession'
import { DropdownButton, DropdownButtonProps } from 'components/dropdown/DropdownButton'
import { DropdownLink } from 'components/dropdown/DropdownLink'
import { useErrorHandler } from 'components/error'
import { confirm } from 'components/modals/confirm'
import { useApolloClient } from 'graphql/hooks'
import {
  AgendaEmConflitoDocument,
  useAlterarAtivacaoAcessoMutation,
  useDesativarConfiguracaoAgendaOnlineLotacaoMutation,
  useExcluirConfiguracaoAgendaLotacaoMutation,
  useExcluirLotacaoMutation,
} from 'graphql/hooks.generated'
import { AgendaEmConflitoQuery, AgendaEmConflitoQueryVariables } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useRouteMatch } from 'react-router'
import Permissions from 'types/Permissions'

import { LotacaoModel } from '../model'
import {
  getInconsistenciasLotacaoAgendaOnline,
  InconsistenciasAgendaOnlineLotacaoModel,
} from './LotacaoAgendaOnlineStatus'

export interface LotacaoAcessoCardDropdownProps extends Partial<DropdownButtonProps> {
  agendaOnlineAtiva: boolean
  lotacao: LotacaoModel
  possuiCns: boolean
  refetch(): any
}

export function LotacaoAcessoCardDropdown(props: Readonly<LotacaoAcessoCardDropdownProps>) {
  const { analytics } = useFirebase()

  const match = useRouteMatch()
  const alert = useAlert()
  const { hasAuthorization } = useSession({ fetchPolicy: 'cache-only' })

  const handleRejection = useErrorHandler()
  const [excluirLotacao] = useExcluirLotacaoMutation()
  const [alterarAtivacaoAcesso] = useAlterarAtivacaoAcessoMutation()
  const [excluirCfgAgendaMutation] = useExcluirConfiguracaoAgendaLotacaoMutation()
  const [desativarAgendaOnlineMutation] = useDesativarConfiguracaoAgendaOnlineLotacaoMutation()
  const apollo = useApolloClient()

  const { agendaOnlineAtiva, lotacao, possuiCns, refetch, ...rest } = props
  const inconsistenciasLotacaoAgendaOnline = getInconsistenciasLotacaoAgendaOnline(
    lotacao as InconsistenciasAgendaOnlineLotacaoModel
  )
  const lotacaoElegivelAgendaOnline = !inconsistenciasLotacaoAgendaOnline
  const permissaoExclusao = lotacao.actions.permissaoExclusao
  const permissaoAtivacaoInativacao = lotacao.actions.permissaoAtivacaoInativacao
  const permissaoConfigurarAgenda = lotacao.actions.permissaoConfigurarAgenda
  const permissaoConfigurarAgendaOnline = lotacao.actions.permissaoConfigurarAgendaOnline
  const permissaoFecharAgenda = lotacao.actions.permissaoFecharAgenda

  const handleAlternarAtivacaoLotacao = () => {
    if (lotacao.ativo) {
      alternarAtivacaoLotacao()
    } else {
      ativarLotacaoAgendaSemConflito()
    }
  }

  const alternarAtivacaoLotacao = () => {
    return alterarAtivacaoAcesso({ variables: { id: lotacao.id } })
      .then((ret) => {
        const data = ret && ret.data
        lotacao.hasAgendamentoFuturo && !data.alterarAtivacaoAcesso
          ? alert('warning', 'Lotação foi inativada, mas possuía agendamentos futuros. Verifique no módulo Agenda.')
          : alert('success', `Lotação ${!data.alterarAtivacaoAcesso ? 'inativada' : 'ativada'} com sucesso.`)

        refetch()
      })
      .finally(refetch())
      .catch(handleRejection)
  }

  const ativarLotacaoAgendaSemConflito = () => {
    apollo
      .query<AgendaEmConflitoQuery, AgendaEmConflitoQueryVariables>({
        query: AgendaEmConflitoDocument,
        variables: { id: lotacao.id },
        fetchPolicy: 'network-only',
      })
      .then((res) => {
        if (res.data.agendaEmConflito) {
          confirm({
            title: 'Deseja ativar a lotação e excluir a agenda?',
            body: 'Esta lotação possui uma agenda com conflitos de horário.',
            confirmLabel: 'Ativar e excluir agenda',
            type: 'danger',
            onConfirm: async () => {
              await excluirAgendaSemConfirmacao()
              await alternarAtivacaoLotacao()
            },
          })()
        } else {
          alternarAtivacaoLotacao()
        }
      })
      .catch(handleRejection)
  }

  const excluirAgendaSemConfirmacao = () => {
    return excluirCfgAgendaMutation({ variables: { id: lotacao.id } })
      .then(() => {
        alert('success', 'A agenda da lotação foi excluída com sucesso.')
        props.refetch()
      })
      .catch(handleRejection)
  }

  const handleExcluirLotacao = confirm({
    title: 'Deseja excluir a lotação?',
    confirmLabel: 'Excluir',
    type: 'danger',
    onConfirm: () => {
      return excluirLotacao({ variables: { lotacaoId: lotacao.id } })
        .then(() => {
          alert('success', 'Lotação excluída com sucesso.')
          refetch()
        })
        .catch(handleRejection)
    },
  })

  const renderConfirmBody = () => {
    if (lotacao.hasAgendamentoFuturo && lotacao.hasConfiguracaoAgendaOnline) {
      return (
        <>
          <Text component='p' style={{ marginBottom: '1rem' }}>
            Existem agendamentos futuros para esta lotação. Após a exclusão da configuração ainda será possível
            visualizá-los na agenda da lotação do profissional.
          </Text>
          <Text component='p'>A configuração de agenda online deste profissional também será excluída.</Text>
        </>
      )
    }

    if (lotacao.hasAgendamentoFuturo) {
      return 'Existem agendamentos futuros para esta lotação. Após a exclusão da configuração ainda será possível visualizá-los na agenda da lotação do profissional.'
    }
    if (lotacao.hasConfiguracaoAgendaOnline) {
      return 'A configuração de agenda online deste profissional também será excluída.'
    }
    return ''
  }

  const handleExcluirAgenda = () => {
    confirm({
      title: 'Deseja excluir a agenda desta lotação?',
      body: renderConfirmBody(),
      confirmLabel: 'Excluir',
      type: 'danger',
      onConfirm: excluirAgendaSemConfirmacao,
    })()
  }

  const handleDesativarAgendaOnline = () => {
    confirm({
      title: 'Deseja excluir a agenda online desta lotação?',
      confirmLabel: 'Excluir',
      type: 'danger',
      onConfirm: () => {
        return desativarAgendaOnlineMutation({ variables: { id: lotacao.id } })
          .then(() => {
            alert('success', 'A agenda online da lotação foi excluída com sucesso.')
            analytics.logEvent('excluir_config_agenda_PROF')
            props.refetch()
          })
          .catch(handleRejection)
      },
    })()
  }

  const txtAtivarInativar = lotacao.ativo ? 'Inativar' : 'Ativar'
  const txtProfSemCNS = !lotacao.ativo && !possuiCns && 'Profissional sem CNS não pode ter lotação'

  const items: React.ReactNode[] = []
  if (hasAuthorization(Permissions.visualizarProfissionais.visualizarAcessos.cadastrarEditarEExcluirLotacao)) {
    items.push(
      <Tooltip key='ativar/inativar' text={permissaoAtivacaoInativacao.hint ?? txtProfSemCNS}>
        <DropdownItem
          key='ativar/inativar'
          onClick={handleAlternarAtivacaoLotacao}
          disabled={!permissaoAtivacaoInativacao.enabled || (!lotacao.ativo && !possuiCns)}
        >
          {txtAtivarInativar}
        </DropdownItem>
      </Tooltip>
    )
  }
  if (hasAuthorization(Permissions.visualizarProfissionais.visualizarAcessos.configurarAgenda)) {
    if (lotacao.ativo) {
      items.push(
        <Tooltip key='criarAgenda/editarAgenda' text={permissaoConfigurarAgenda.hint}>
          <DropdownLink
            key='criarAgenda/editarAgenda'
            to={`${match.url}/configuracaoAgenda/${lotacao.id}`}
            disabled={!permissaoConfigurarAgenda.enabled}
            onClick={() => {
              lotacao.hasConfiguracaoAgenda && analytics.logEvent('editar_config_agenda_PROF')
            }}
          >
            {lotacao.hasConfiguracaoAgenda ? 'Editar configuração de agenda' : 'Criar configuração de agenda'}
          </DropdownLink>
        </Tooltip>
      )
    } else {
      items.push(
        <Tooltip
          key='criarAgenda/editarAgenda'
          text={
            !lotacao.ativo
              ? 'Não é possível configurar agenda em lotação inativa'
              : permissaoConfigurarAgenda.hint || ''
          }
        >
          <DropdownItem disabled={!lotacao.ativo || permissaoConfigurarAgenda.enabled}>
            {lotacao.hasConfiguracaoAgenda ? 'Editar configuração de agenda' : 'Criar configuração de agenda'}{' '}
          </DropdownItem>
        </Tooltip>
      )
    }
    if (lotacao.hasConfiguracaoAgenda) {
      items.push(
        <Tooltip key='excluirAgenda' text={permissaoConfigurarAgenda.hint}>
          <DropdownItem
            key='excluirAgenda'
            type='danger'
            onClick={handleExcluirAgenda}
            disabled={!permissaoConfigurarAgenda.enabled}
          >
            Excluir agenda
          </DropdownItem>
        </Tooltip>
      )
    }
  }
  if (agendaOnlineAtiva) {
    if (hasAuthorization(Permissions.visualizarProfissionais.visualizarAcessos.configurarAgendaOnline)) {
      items.push(
        <Tooltip
          key='criarAgendaOnline/editarAgendaOnline'
          text={
            lotacaoElegivelAgendaOnline
              ? ''
              : inconsistenciasLotacaoAgendaOnline || permissaoConfigurarAgendaOnline.hint
          }
        >
          <DropdownLink
            to={`${match.url}/configuracaoAgendaOnline/${lotacao.id}`}
            disabled={!lotacaoElegivelAgendaOnline || !permissaoConfigurarAgendaOnline.enabled}
            onClick={() =>
              lotacao.hasConfiguracaoAgendaOnline && analytics.logEvent('editar_config_agenda_online_PROF')
            }
          >
            {lotacao.hasConfiguracaoAgendaOnline
              ? 'Editar configuração de agenda online'
              : 'Criar configuração de agenda online'}
          </DropdownLink>
        </Tooltip>
      )
      if (lotacao.hasConfiguracaoAgendaOnline) {
        items.push(
          <Tooltip key='excluirAgendaOnline' text={permissaoConfigurarAgendaOnline.hint}>
            <DropdownItem
              key='excluirAgendaOnline'
              type='danger'
              onClick={handleDesativarAgendaOnline}
              disabled={!permissaoConfigurarAgendaOnline.enabled}
            >
              Excluir agenda online
            </DropdownItem>
          </Tooltip>
        )
      }
    }
  }
  if (hasAuthorization(Permissions.visualizarProfissionais.visualizarAcessos.fecharAgenda)) {
    if (lotacao.ativo && lotacao.hasConfiguracaoAgenda) {
      items.push(
        <Tooltip key='visualizarFechamentoAgenda' text={permissaoFecharAgenda.hint}>
          <DropdownLink
            key='visualizarFechamentoAgenda'
            to={`${match.url}/acessos/fechamento/${lotacao.id}`}
            disabled={!permissaoFecharAgenda.enabled}
            onClick={() => {
              analytics.logEvent('editar_ver_fechamento_PROF')
            }}
          >
            Visualizar fechamentos da agenda
          </DropdownLink>
        </Tooltip>
      )
    }
  }
  if (hasAuthorization(Permissions.visualizarProfissionais.visualizarAcessos.cadastrarEditarEExcluirLotacao)) {
    items.push(
      <Tooltip key='excluir' text={permissaoExclusao.hint}>
        <DropdownItem type='danger' onClick={handleExcluirLotacao} disabled={!permissaoExclusao.enabled}>
          Excluir
        </DropdownItem>
      </Tooltip>
    )
  }
  if (items.length === 0) {
    return null
  }

  return <DropdownButton {...rest}>{items}</DropdownButton>
}
